/**
 * Pure H · Canonical KPI hook (P0-5 consumer).
 *
 * Every screen that shows revenue / order counts / SKU counts MUST call
 * this hook. It reads the single computed source from `/api/pureh/kpi/summary`
 * so Owner Cockpit, Finance Overview, Commerce Overview and Executive
 * Dashboard cannot drift apart again.
 */
import { useEffect, useState } from "react";
import { getActiveMarketHeader } from "../services/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pureh`;

type Val = { value: number | null; unit: string | null; hasData: boolean };

export type CanonicalKpi = {
  ventureId:   string;
  marketId?:   string | null;
  currency?:   string;
  dataMode:    "demo" | "live";
  generatedAt: string;
  revenue:  { monthlyEGP: Val; monthly?: Val; currency?: string; scope: string; sourceCollection: string };
  orders:   { last30d:    Val; scope: string; sourceCollection: string };
  products: { liveSkus:   Val; launchesInFlight: Val; scope: string; sourceCollection: string };
  approvals:{ assignedToMe: Val; totalPending: Val; scope: string; sourceCollection: string };
  qc:       { passRate90d: Val; scope: string; sourceCollection: string };
};

export function useCanonicalKpi(ventureId: string = "orvan") {
  const [data, setData] = useState<CanonicalKpi | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = getActiveMarketHeader();
        const headers: Record<string, string> = m ? { "X-Market-Id": m } : {};
        const r = await fetch(`${API}/kpi/summary?venture_id=${encodeURIComponent(ventureId)}`, {
          credentials: "include",
          headers,
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d: CanonicalKpi = await r.json();
        if (!cancelled) { setData(d); setError(null); }
      } catch (e: any) {
        if (!cancelled) setError(String(e?.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ventureId]);

  return { data, error, loading };
}

/** Compact source-of-truth chip rendered next to any canonical KPI value. */
export function canonicalLabel(collection: string): string {
  return `canonical · ${collection}`;
}
